import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import SeoHead from '../components/SeoHead';
import { SketchPageShell, SketchNavBar, SketchButton } from '../components/sketchy';
import AppNavBar from '../components/AppNavBar';
import { formatTrackLimit, formatCollaboratorLimit } from '../constants/tierLimits';
import { MUSIC_SEO } from '../seo/seoConfig';
import { musicSoftwareApplicationJsonLd } from '../seo/jsonLd';
import ga from '../services/analytics';
import { useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Upgrade from './Upgrade';
import CassetteLoader from '../components/CassetteLoader';

const PublicPricingPage = () => {
  const { t } = useTranslation();
  const { user, storageLinked, loading } = useAuth();

  useEffect(() => {
    if (!loading && (!user || !storageLinked)) {
      ga.event('pricing_page_viewed');
    }
  }, [loading, user, storageLinked]);

  if (loading) {
    return <CassetteLoader />;
  }

  if (user && storageLinked) {
    return (
      <SketchPageShell className="min-h-screen flex flex-col">
        <AppNavBar />
        <main className="relative flex-1 text-[var(--color-text)]">
          <Upgrade />
        </main>
      </SketchPageShell>
    );
  }

  const tiers = [
    {
      name: t('upgrade.starter'),
      price: t('upgrade.pricing.free'),
      highlight: false,
      features: [
        t('upgrade.features.fullTrackEditing'),
        formatTrackLimit('starter'),
        formatCollaboratorLimit('starter'),
        t('upgrade.features.tryFullFlow'),
      ],
    },
    {
      name: t('upgrade.artist'),
      price: `$5${t('upgrade.pricing.monthly')}`,
      priceNote: t('upgrade.cassetteCloudAddon', { price: '$10' }),
      highlight: true,
      badge: t('upgrade.badges.mostPopular'),
      features: [
        t('upgrade.features.fullTrackEditing'),
        formatTrackLimit('artist'),
        formatCollaboratorLimit('artist'),
        t('upgrade.features.allFeaturesIncluded'),
      ],
    },
    {
      name: t('upgrade.studio'),
      price: `$25${t('upgrade.pricing.monthly')}`,
      priceNote: t('upgrade.cassetteCloudAddon', { price: '$50' }),
      highlight: false,
      badge: t('upgrade.badges.forTeams'),
      features: [
        t('upgrade.features.fullTrackEditing'),
        formatTrackLimit('studio'),
        formatCollaboratorLimit('studio'),
        t('upgrade.features.futureFeaturesAutoAdded'),
      ],
    },
  ];

  return (
    <SketchPageShell className="min-h-screen flex flex-col">
      <SeoHead meta={MUSIC_SEO.upgrade} jsonLd={musicSoftwareApplicationJsonLd()} />
      <SketchNavBar>
        <div className="max-w-6xl mx-auto w-full">
          <div className="flex h-16 items-center">
            <Link
              to="/"
              className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-[var(--color-hover)] transition-colors group"
            >
              <ArrowLeftIcon className="h-5 w-5 group-hover:-translate-x-1 transition-transform duration-200" />
              <span className="text-sm font-medium">{t('buttons.back')}</span>
            </Link>
          </div>
        </div>
      </SketchNavBar>

      <main className="flex-1 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <h1 className="text-3xl font-bold mb-2 text-center">{t('upgrade.choosePlan')}</h1>
          <div className="max-w-2xl mx-auto mb-8 space-y-3 text-center">
            <p className="text-sm text-[var(--color-muted)]">{t('upgrade.byodNote')}</p>
            <p className="text-sm text-[var(--color-text)] rounded-lg border px-4 py-3 text-left" style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-hover)' }}>
              {t('upgrade.byodWhyCheaper')}
            </p>
          </div>

          <div className="flex flex-col lg:flex-row gap-6">
            {tiers.map((tier) => (
              <div
                key={tier.name}
                className={`flex-1 border-2 rounded-xl p-6 shadow relative flex flex-col ${
                  tier.highlight ? 'border-emerald-500' : 'border-[var(--color-border)]'
                } bg-[var(--color-surface)]`}
              >
                {tier.badge && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-xs font-bold shadow bg-emerald-600 text-white">
                    {tier.badge}
                  </div>
                )}
                <h2 className="text-2xl font-bold mb-2">{tier.name}</h2>
                <div className="text-3xl font-bold mb-2">{tier.price}</div>
                {'priceNote' in tier && tier.priceNote ? (
                  <p className="text-xs text-[var(--color-muted)] mb-4">{tier.priceNote}</p>
                ) : (
                  <div className="mb-4" />
                )}
                <ul className="mb-6 space-y-2 text-sm flex-1">
                  {tier.features.map((f) => (
                    <li key={f}>✔️ {f}</li>
                  ))}
                </ul>
                <Link to="/auth/login" className="block mt-auto">
                  <SketchButton variant="primary" className="w-full">
                    {tier.price === t('upgrade.pricing.free')
                      ? t('landing.cta.button')
                      : `${t('upgrade.subscribe')} ${tier.name}`}
                  </SketchButton>
                </Link>
              </div>
            ))}
          </div>

          <p className="mt-8 text-center text-xs text-[var(--color-muted)]">* {t('upgrade.disclaimer')}</p>
          <p className="mt-4 text-center text-sm">
            <Link to="/help" className="text-[var(--brand-from)] hover:underline">
              {t('docs.hub.helpLink')}
            </Link>
          </p>
        </div>
      </main>
    </SketchPageShell>
  );
};

export default PublicPricingPage;
